import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SDAGoBack from "./SDAGoBack";
import SDASteps from "./SDASteps";
import ContactUsModal from "../../components/ContactUsModal";
import { SDATestModeBanner, SDATestSkipButton } from "./sdaTestMode";
import { useSDAInvite } from "./SDAContext";
import { parseLocalDate } from "@ui/shared";
import { formatCurrency } from "../../utils/formatCurrency";

const formatUSD = (n?: number) =>
  typeof n === "number" ? formatCurrency(n) : "—";

// Quote DATE-only fields (coverageStartDate / coverageEndDate).
const formatDate = (s?: string) =>
  s ? parseLocalDate(s).toLocaleDateString() : "—";

const SDAQuote: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    invite,
    answers,
    quote,
    quoteLoading,
    quoteError,
    quoteAttempted,
    loadQuote,
  } = useSDAInvite();
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    // Already-quoted invites are auto-loaded by the context; nothing to do
    // here. If the user has no persisted quote AND hasn't answered the
    // underwriting questions in this session, send them back.
    if (!invite) return;
    if (!invite.quoteId && !answers) {
      navigate(`/sda/${uuid}/underwriting`, { replace: true });
      return;
    }
    // Kick off the quote once. `quoteAttempted` stays true after a failed
    // call, so a server error doesn't immediately re-trigger the request.
    // The user can retry via the button in the error block.
    if (!quote && !quoteLoading && !quoteAttempted) {
      loadQuote();
    }
  }, [
    invite,
    answers,
    quote,
    quoteLoading,
    quoteAttempted,
    loadQuote,
    navigate,
    uuid,
  ]);

  return (
    <div>
      <SDAGoBack />

      <SDATestModeBanner />

      <SDASteps current={5} />

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-primary">
            {t("sda.quote.title")}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {t("sda.quote.subtitle")}
          </p>
        </div>

        <div className="p-5">
          {quoteLoading && (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <svg
                className="h-10 w-10 animate-spin text-primary"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              <p className="text-sm text-muted">
                {t("sda.quote.calculating")}
              </p>
            </div>
          )}

          {quoteError && !quoteLoading && (
            <div className="my-6 rounded-lg border border-red-100 bg-red-50 px-5 py-4 text-sm">
              <p className="font-semibold text-danger">
                {t("sda.quote.errorTitle")}
              </p>
              <p className="mt-1 text-gray-700">
                {t("sda.quote.errorBody")}
              </p>
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                className="mt-3 cursor-pointer rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light"
              >
                {t("sda.quote.contactUs")}
              </button>
            </div>
          )}

          {quote && !quoteLoading && (
            <>
              {/* Total amount */}
              <div className="mb-6 text-center">
                <p className="text-5xl font-bold text-primary">
                  {formatUSD(quote.total)}
                </p>
                <p className="mt-1 text-sm text-muted">
                  {t("sda.quote.totalSubtitle")}
                </p>
              </div>

              {/* Coverage summary */}
              <div className="mb-6 grid grid-cols-1 gap-x-6 gap-y-2 rounded-lg bg-gray-50 p-4 text-sm sm:grid-cols-3">
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-muted">
                      {t("sda.quote.creditAmount")}
                    </p>
                    <span className="group relative inline-flex">
                      <button
                        type="button"
                        aria-label={t("sda.quote.creditAmountInfoAria")}
                        className="cursor-help text-muted hover:text-primary focus:text-primary focus:outline-none"
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <span
                        role="tooltip"
                        className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-64 -translate-x-1/2 rounded-lg bg-primary px-3 py-2 text-left text-xs font-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t("sda.quote.creditAmountTooltip"),
                          }}
                        />
                      </span>
                    </span>
                  </div>
                  <p className="font-semibold text-primary">
                    {formatUSD(quote.coverageAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted">{t("sda.quote.startDate")}</p>
                  <p className="font-medium">
                    {formatDate(quote.coverageStartDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted">{t("sda.quote.endDate")}</p>
                  <p className="font-medium">
                    {formatDate(quote.coverageEndDate)}
                  </p>
                </div>
              </div>

              {/* Description — legal prose with inline <strong> + <a>, kept
                  in English. Translate with <Trans> when es copy is finalized. */}
              <div className="mb-6 space-y-3 text-sm text-gray-700">
                <p>
                  For just <strong>{formatUSD(quote.quote)}</strong>, you are
                  guaranteeing a Line of Credit up to{" "}
                  <strong>{formatUSD(quote.coverageAmount)}</strong> for your
                  Landlord to potentially call on during the period outlined
                  above. This coverage protects your Landlord from any unpaid
                  rent and/or damages beyond normal wear &amp; tear related to
                  your lease. Please note that you remain responsible for these
                  expenses and must renew coverage with Lighthouse at the
                  beginning of each new lease term.
                </p>
                <p>
                  There are no monthly fees. Renewals are automatically
                  processed using the payment method provided. See our{" "}
                  <a
                    href="/faq"
                    rel="noreferrer"
                    target="_blank"
                    className="text-info underline hover:text-sky-600"
                  >
                    Tenant FAQ
                  </a>{" "}
                  for more information on coverage.
                </p>
              </div>

              {/* Fee breakdown */}
              <div className="mb-6 space-y-2 border-t border-gray-100 pt-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted">
                    {t("sda.quote.fees.issuingFee")}
                  </span>
                  <span className="font-medium">{formatUSD(quote.quote)}</span>
                </div>
                {quote.policyFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted">
                      {t("sda.quote.fees.policyFee")}
                    </span>
                    <span className="font-medium">
                      {formatUSD(quote.policyFee)}
                    </span>
                  </div>
                )}
                {quote.tax > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted">
                      {t("sda.quote.fees.tax")}
                    </span>
                    <span className="font-medium">{formatUSD(quote.tax)}</span>
                  </div>
                )}
                {quote.stampingFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted">
                      {t("sda.quote.fees.stampingFee")}
                    </span>
                    <span className="font-medium">
                      {formatUSD(quote.stampingFee)}
                    </span>
                  </div>
                )}
                {quote.companyPolicyFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted">
                      {t("sda.quote.fees.companyPolicyFee")}
                    </span>
                    <span className="font-medium">
                      {formatUSD(quote.companyPolicyFee)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted">
                    {t("sda.quote.fees.processingFee")}
                  </span>
                  <span className="font-medium">
                    {formatUSD(quote.billingFee)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-100 pt-2 text-base">
                  <span className="font-semibold text-primary">
                    {t("sda.quote.fees.total")}
                  </span>
                  <span className="font-semibold text-primary">
                    {formatUSD(quote.total)}
                  </span>
                </div>
              </div>

              {/* Legal */}
              <div className="rounded-lg bg-gray-50 p-4 text-xs leading-relaxed text-muted">
                By purchasing coverage through Lighthouse, you agree to abide by
                our terms and conditions outlined in the{" "}
                <a
                  rel="noreferrer"
                  href="https://media.mylighthouse.co/docs/tenant-acknowledgment-agreement.pdf"
                  target="_blank"
                  className="text-info underline hover:text-sky-600"
                >
                  Tenant Acknowledgement Agreement
                </a>{" "}
                and confirm that you are the rightful owner of the payment
                method provided, hereby authorizing this transaction and
                waiving your right to dispute this payment as fraudulent at any
                time. If payment is refunded by Lighthouse for any reason,
                processing fees will not be included.
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <SDATestSkipButton
            onSkip={() => navigate(`/sda/${uuid}/signatures`)}
          />
          <button
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!quote || quoteLoading}
            onClick={() => navigate(`/sda/${uuid}/signatures`)}
          >
            {t("sda.quote.continue")}
          </button>
        </div>
      </div>

      <ContactUsModal show={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
};

export default SDAQuote;
